// ── V143: 다른 업체에 '이 식재료 있나요?' 묻는 목록 (위챗용) ───────────────────────
// 온라인(快驴进货·타오바오)으로 사던 식재료를 오프라인 업체에 넘기려고 물어볼 때 쓴다.
//   · 중국어로: 품목 이름 · 규격 · 브랜드(제조사) · 타오바오 등 구매 링크
//   · 가격은 넣지 않는다 (우리가 사는 값을 알려 주지 않기 위해)
//   · 링크는 줄 하나에 링크만 따로 둔다 → 위챗이 눌러서 열 수 있는 링크로 인식한다
//     (링크 앞뒤에 한자가 붙어 있으면 링크로 안 잡히는 경우가 있다)
//   · 길면 나눠서 보낸다 (각 묶음에 (1/2) 표시)
import { specToZh } from '../i18n/units';
import { safeLinkHref } from './purchaseLink';
import { normalizeIngredientName } from './ingredientMatch';

const s = (x) => String(x == null ? '' : x).trim();
const hasHangul = (x) => /[가-힣]/.test(String(x || ''));
const zhOnly = (x) => { const v = s(x); return v && !hasHangul(v) ? v : ''; };

export const CATEGORY_ZH = { '신선식품': '生鲜', '공산품': '干货 · 调料', '소모품': '消耗品' };
const CATEGORY_ORDER = ['신선식품', '공산품', '소모품'];

export function defaultGreeting(bizName) {
  const bn = s(bizName);
  return `您好！我们是${bn ? ` ${bn} ` : ''}韩国餐厅。\n以下食材请问贵店有没有货？有的话麻烦告诉我们价格，以及能不能送货。谢谢！`;
}

/** 문의 대상이 될 수 있는 단가표 줄 (반제품·이름 없는 줄 제외) */
export function inquiryRows(prices, vendors) {
  const vs = new Set((vendors || []).map(s));
  return (prices || []).filter(p => p && s(p.name) && p.category !== '반제품' && vs.has(s(p.vendor)));
}

/** 한 품목의 중국어 정보 */
export function itemInfo(p, { withKo = false, withBrand = true } = {}) {
  const ko = s(p.name);
  const zh = zhOnly(p.name_zh);
  const name = zh ? (withKo ? `${zh} / ${ko}` : zh) : ko;
  const specRaw = s(p.spec);
  const spec = specRaw ? (specToZh(specRaw) || zhOnly(p.spec_zh) || specRaw) : '';
  const unitRaw = s(p.unit);
  const unit = !spec && unitRaw ? (zhOnly(p.unit_zh) || unitRaw) : '';
  const brand = withBrand ? (zhOnly(p.maker_zh) || zhOnly(p.maker) || '') : '';
  const link = safeLinkHref(p.link) || '';
  return { name, spec, unit, brand, link };
}

/**
 * 위챗으로 보낼 글 — 길면 여러 개로 나눈다
 * @returns {{ parts: string[], count: number }}
 */
export function buildInquiryText(rows, { greeting = '', withKo = false, withBrand = true, maxLen = 1800 } = {}) {
  // 같은 품목(중국어 이름·규격이 같음)이 발주처별로 두 번 있으면 한 번만 (링크 있는 쪽)
  const seen = new Map();
  for (const p of rows || []) {
    const info = itemInfo(p, { withKo, withBrand });
    const k = normalizeIngredientName(info.name) + '|' + normalizeIngredientName(info.spec);
    const prev = seen.get(k);
    if (!prev || (!prev.info.link && info.link)) seen.set(k, { p, info });
  }
  const items = [...seen.values()];
  const catOf = (p) => (CATEGORY_ORDER.includes(p.category) ? p.category : '기타');
  const order = [...CATEGORY_ORDER, '기타'];
  items.sort((a, b) => order.indexOf(catOf(a.p)) - order.indexOf(catOf(b.p)));

  // 품목 덩어리 만들기 (번호는 전체 기준)
  const blocks = [];
  let lastCat = null;
  items.forEach((it, i) => {
    const c = catOf(it.p);
    const lines = [];
    if (c !== lastCat) { lines.push(`【${CATEGORY_ZH[c] || '其他'}】`); lastCat = c; }
    lines.push(`${i + 1}. ${it.info.name}${it.info.brand ? `（品牌：${it.info.brand}）` : ''}`);
    if (it.info.spec) lines.push(`   规格：${it.info.spec}`);
    else if (it.info.unit) lines.push(`   单位：${it.info.unit}`);
    if (it.info.link) { lines.push(''); lines.push(it.info.link); lines.push(''); }   // 링크는 줄 하나에 링크만
    blocks.push({ cat: c, text: lines.join('\n') });
  });

  const head = s(greeting);
  const tail = `共 ${items.length} 项`;
  const parts = [];
  let cur = head ? [head, ''] : [];
  let curLen = cur.join('\n').length;
  let curCat = null;
  for (const b of blocks) {
    let text = b.text;
    // 새 묶음 첫 품목인데 분류 머리줄이 없으면 붙여 준다
    if (!cur.length && !text.startsWith('【')) text = `【${CATEGORY_ZH[curCat] || '其他'}】\n${text}`;
    if (curLen + text.length + 1 > maxLen && cur.length && cur.some(l => /^\d+\./m.test(l))) {
      parts.push(cur.join('\n').replace(/\n{3,}/g, '\n\n').trim());
      cur = [];
      curLen = 0;
      if (!text.startsWith('【')) text = `【${CATEGORY_ZH[b.cat] || '其他'}】\n${text}`;
    }
    cur.push(text);
    curLen += text.length + 1;
    curCat = b.cat;
  }
  cur.push('', tail);
  parts.push(cur.join('\n').replace(/\n{3,}/g, '\n\n').trim());
  if (parts.length > 1) return { parts: parts.map((t, i) => `(${i + 1}/${parts.length})\n${t}`), count: items.length };
  return { parts, count: items.length };
}
